import React, {useCallback, useEffect, useState} from "react";
import {Card, Modal} from "react-bootstrap";
import {Location, Photo, User} from "../types/Types";
import {collection, getDocs, limit, query, where} from "@firebase/firestore";
import {useParams, useNavigate} from "react-router-dom";
import {db} from "../../firebase/FirebaseProvider";
import {generateTags, PHOTOS_LIMIT, logDataLoaded} from "../util/Util";
import PhotoRow from "../locations/profile/PhotoRow";
import AuthCheck from "../auth/AuthCheck";
import PageTracking from "../auth/PageTracking";
import PhotoTagInput from "./PhotoTagInput";
import {updatePhotoDescription, hidePhoto, addPhotoTags, clearPhotoTags, updatePhotoLocation} from "../util/PhotoUtil";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowLeft} from "@fortawesome/free-solid-svg-icons";
import {V2GlassPanel} from "../shared/v2/V2Glass";
import V2PhotoRow from "../shared/v2/V2PhotoRow";

interface Props {
    currentUser: User | undefined,
    usersMap: Map<string, User>,
    likesMap: Map<string, boolean>,
    locations: Array<Location>
}

interface V2PhotoDisplayProps {
    photo: Photo;
    photoUser: User | undefined;
    currentUser: User;
    likesMap: Map<string, boolean>;
    canGoBack: boolean;
    onBack: () => void;
    onImageClick: (event: React.MouseEvent) => void;
}

function V2PhotoDisplay(props: V2PhotoDisplayProps) {
    const [likeCount, setLikeCount] = useState<number>(props.photo.likeCount || 0);

    useEffect(() => {
        setLikeCount(props.photo.likeCount || 0);
    }, [props.photo.id, props.photo.likeCount]);

    const updateLikeCount = useCallback((_entityId: string, adjustment: number) => {
        setLikeCount(currentCount => Math.max(0, currentCount + adjustment));
    }, []);

    return (
        <div className="photo-editor-v2">
            {props.canGoBack && (
                <V2GlassPanel className="photo-editor-v2-toolbar">
                    <button className="photo-editor-v2-back-button" type="button" onClick={props.onBack}>
                        <FontAwesomeIcon icon={faArrowLeft} />
                        <span>Back</span>
                    </button>
                    <span className="photo-editor-v2-toolbar-hint">Click the photo to see the full size version</span>
                </V2GlassPanel>
            )}
            <V2PhotoRow
                photo={props.photo}
                photoUser={props.photoUser}
                currentUser={props.currentUser}
                likesMap={props.likesMap}
                likeCount={likeCount}
                onLikeCountChange={updateLikeCount}
                locationPlacement="bottom"
                onImageClick={props.onImageClick}
                className="photo-editor-v2-image-surface"
                imageStageClassName="photo-editor-v2-image-stage"
                imageClassName="photo-editor-v2-image"
                bodyClassName="photo-editor-v2-meta"
            />
        </div>
    );
}

function PhotoEditor(props: Props) {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [photo, setPhoto] = useState<Photo>();
    const [newTag, setNewTag] = useState<string>("");
    const [newDescription, setNewDescription] = useState<string>("");
    const [showModal, setShowModal] = useState<boolean>(false);
    const [imageLoaded, setImageLoaded] = useState<boolean>(false);
    const [canGoBack, setCanGoBack] = useState<boolean>(false);
    const [isDeletingPhoto, setIsDeletingPhoto] = useState<boolean>(false);
    const [isUpdatingDescription, setIsUpdatingDescription] = useState<boolean>(false);
    const [isAddingTags, setIsAddingTags] = useState<boolean>(false);
    const [isClearingTags, setIsClearingTags] = useState<boolean>(false);
    const [isUpdatingLocation, setIsUpdatingLocation] = useState<boolean>(false);
    const [locationSearch, setLocationSearch] = useState<string>("");
    const [selectedLocation, setSelectedLocation] = useState<Location | undefined>(undefined);
    const [showLocationOptions, setShowLocationOptions] = useState<boolean>(false);
    const isUiV2 = !!props.currentUser?.enableUiV2;

    useEffect(() => {
        // Check if there's a previous page in history
        setCanGoBack(window.history.length > 1);
    }, []);

    const loadPhoto = useCallback(async() => {
        if(!props.currentUser || !id) {
            return;
        }
        const photosOnceCollection = query(collection(db, 'photos'),
            where('id', '==', id),
            limit(PHOTOS_LIMIT));
        const photosOnceCollectionSnapshot = await getDocs(photosOnceCollection);
        let photoCount = 0;
        photosOnceCollectionSnapshot.forEach((doc) => {
            let tempPhoto = doc.data() as Photo;
            tempPhoto.id = doc.id;
            setPhoto(tempPhoto);
            photoCount++;
            // Populate the tag input field with existing tags
            if (tempPhoto.tags && tempPhoto.tags.length > 0) {
                setNewTag(tempPhoto.tags.join(', '));
            }
            setLocationSearch(tempPhoto.locationName);
            setSelectedLocation(undefined);
        });
        logDataLoaded('editor photo', photoCount);
    }, [props.currentUser, id]);

    useEffect(() => {
        (async () => {
            await loadPhoto();
        })();
    }, [loadPhoto]);

    const handleClose = () => {
        setShowModal(false);
        setImageLoaded(false);
    };

    const handleImageClick = (e: React.MouseEvent) => {
        // Only open modal if clicking on the img element
        if ((e.target as HTMLElement).tagName === 'IMG') {
            setShowModal(true);
            // Preload the image
            if (photo) {
                const img = new Image();
                img.onload = () => setImageLoaded(true);
                img.src = photo.fullSizeUrl;
            }
        }
    };

    const handleBackClick = () => {
        navigate(-1);
    };

    const handleDeletePhoto = async () => {
        if (!photo) return;
        setIsDeletingPhoto(true);
        try {
            await hidePhoto(photo.id, () => {
                navigate(`/location/${photo.locationId}/photos`);
            });
        } finally {
            setIsDeletingPhoto(false);
        }
    };

    const handleUpdateDescription = async () => {
        if (!photo) return;
        setIsUpdatingDescription(true);
        try {
            await updatePhotoDescription(photo.id, newDescription, async () => {
                // Reload the photo to show updated description
                await loadPhoto();
                window.scrollTo(0, 0);
            });
        } finally {
            setIsUpdatingDescription(false);
        }
    };

    const handleAddTags = async () => {
        if (!photo) return;
        setIsAddingTags(true);
        try {
            await addPhotoTags(photo.id, generateTags(newTag), async () => {
                // Reload the photo to show updated tags
                await loadPhoto();
                setNewTag("");
                window.scrollTo(0, 0);
            });
        } finally {
            setIsAddingTags(false);
        }
    };

    const handleClearTags = async () => {
        if (!photo) return;
        setIsClearingTags(true);
        try {
            await clearPhotoTags(photo.id, async () => {
                // Reload the photo to show cleared tags
                await loadPhoto();
                setNewTag("");
                window.scrollTo(0, 0);
            });
        } finally {
            setIsClearingTags(false);
        }
    };

    const getFilteredLocations = () => {
        const search = locationSearch.toLowerCase().trim();
        if (!search) {
            return [];
        }

        return props.locations
            .filter(location => {
                const name = (location.name || '').toLowerCase();
                const city = (location.city || '').toLowerCase();
                const postcode = (location.postcode || '').toLowerCase();
                return name.includes(search) || city.includes(search) || postcode.includes(search);
            })
            .sort((locationA, locationB) => locationA.name.localeCompare(locationB.name))
            .slice(0, 8);
    };

    const handleSelectLocation = (location: Location) => {
        setSelectedLocation(location);
        setLocationSearch(location.name);
        setShowLocationOptions(false);
    };

    const handleUpdateLocation = async () => {
        if (!photo || !selectedLocation || selectedLocation.id === photo.locationId) return;
        setIsUpdatingLocation(true);
        try {
            await updatePhotoLocation(photo.id, selectedLocation.id, selectedLocation.name, async () => {
                await loadPhoto();
                window.scrollTo(0, 0);
            });
        } finally {
            setIsUpdatingLocation(false);
        }
    };

    const filteredLocations = getFilteredLocations();

    return (
        <>
            <PageTracking />
            <AuthCheck />
            <PageTracking />
            <Modal show={showModal && imageLoaded} onHide={handleClose} size="lg" centered>
                <Modal.Body style={{padding: 0}}>
                    {photo && (
                        <img 
                            style={{width: "100%", height: "100%", display: "block"}}
                            src={photo.fullSizeUrl}
                            alt={photo.locationName}
                            onClick={handleClose}
                        />
                    )}
                </Modal.Body>
            </Modal>
            {showModal && !imageLoaded && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1050
                }}>
                    <div className="loading">Loading...</div>
                </div>
            )}
            {isUiV2 ? (
                !photo ? (
                    <V2GlassPanel className="photo-editor-v2-loading">Loading...</V2GlassPanel>
                ) : props.currentUser ? (
                    <V2PhotoDisplay
                        photo={photo}
                        photoUser={props.usersMap.get(photo.userId)}
                        currentUser={props.currentUser}
                        likesMap={props.likesMap}
                        canGoBack={canGoBack}
                        onBack={handleBackClick}
                        onImageClick={handleImageClick}
                    />
                ) : null
            ) : (
                <Card className={"mb-3"}>
                    {canGoBack && photo && (
                        <Card.Header className="header-padding" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                            <button
                                className="btn btn-sm btn-primary"
                                onClick={handleBackClick}
                                style={{padding: '0.25rem 0.5rem'}}
                            >
                                ← Back
                            </button>
                            <small style={{color: '#6c757d', margin: 0}}>Click the photo to see the full size version</small>
                        </Card.Header>
                    )}
                    <div className={"album"}>
                        {!photo && <div className={'loading'}>
                            Loading...
                        </div>}
                        {(photo && props.currentUser) && (
                            <div onClick={handleImageClick}>
                                <PhotoRow
                                    currentUser={props.currentUser}
                                    photo={photo}
                                    photoUser={props.usersMap.get(photo.userId)}
                                    displayLocation={true}
                                    displayEditButton={false}
                                    likesMap={props.likesMap}
                                    disableImageNavigation={true}
                                />
                            </div>
                        )}
                    </div>
                </Card>
            )}
            {!isUiV2 && photo && photo.description && (
                <Card className={"mb-3"}>
                    <Card.Header className="header-padding">
                        <b>Description</b>
                    </Card.Header>
                    <Card.Body>
                        {photo.description}
                    </Card.Body>
                </Card>
            )}
            {photo && props.currentUser && (props.currentUser.photoAdmin || photo.userId === props.currentUser.id) && <>
                <Card className={isUiV2 ? "photo-editor-v2-edit-card v2-glass-surface" : "mb-3"}>
                    <Card.Body>
                        <div className={"mb-3"}>
                            <textarea
                                className="form-control tag-input"
                                rows={3}
                                value={newDescription}
                                onChange={(e) => setNewDescription(e.target.value)}
                                placeholder="Update the description..."
                            />
                        </div>
                        <div className={"text-right"}>
                            <button className={"btn btn-primary"} onClick={handleUpdateDescription} disabled={isUpdatingDescription}>
                                {!isUpdatingDescription && <span>Update description</span>}
                                {isUpdatingDescription && <span>Updating description...</span>}
                            </button>
                        </div>
                    </Card.Body>
                </Card>

                <Card className={isUiV2 ? "photo-editor-v2-edit-card v2-glass-surface" : "mb-3"}>
                    <Card.Body>
                        <div className={"mb-3"}>
                            <PhotoTagInput 
                                tags={newTag}
                                setTags={setNewTag}
                            />
                        </div>
                        <div className={"text-right"}>
                            <button className={"btn btn-danger mr-2"} onClick={handleClearTags} disabled={isClearingTags}>
                                {!isClearingTags && <span>Clear tags</span>}
                                {isClearingTags && <span>Clearing tags...</span>}
                            </button>
                            <button className={"btn btn-primary"} onClick={handleAddTags} disabled={isAddingTags}>
                                {!isAddingTags && <span>Add tags</span>}
                                {isAddingTags && <span>Adding tags...</span>}
                            </button>
                        </div>
                    </Card.Body>
                </Card>

                <Card className={isUiV2 ? "photo-editor-v2-edit-card v2-glass-surface" : "mb-3"}>
                    <Card.Body>
                        <div style={{position: "relative"}}>
                            <input
                                className="form-control tag-input"
                                type="text"
                                value={locationSearch}
                                onChange={(e) => {
                                    setLocationSearch(e.target.value);
                                    setSelectedLocation(undefined);
                                    setShowLocationOptions(true);
                                }}
                                onFocus={() => setShowLocationOptions(true)}
                                placeholder="Search locations..."
                                autoComplete="off"
                            />
                            {showLocationOptions && filteredLocations.length > 0 && (
                                <div className={"list-group"} style={{position: "absolute", zIndex: 1000, width: "100%"}}>
                                    {filteredLocations.map(location => (
                                        <button
                                            key={location.id}
                                            type="button"
                                            className={"list-group-item list-group-item-action"}
                                            onMouseDown={(e) => e.preventDefault()}
                                            onClick={() => handleSelectLocation(location)}
                                        >
                                            <div>{location.name}</div>
                                            <small className={"text-muted"}>{location.address}</small>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className={"mt-2 text-right"}>
                            <button
                                className={"btn btn-primary"}
                                onClick={handleUpdateLocation}
                                disabled={isUpdatingLocation || !selectedLocation || selectedLocation.id === photo.locationId}
                            >
                                {!isUpdatingLocation && <span>Update location</span>}
                                {isUpdatingLocation && <span>Updating location...</span>}
                            </button>
                        </div>
                    </Card.Body>
                </Card>

                <Card className={isUiV2 ? "photo-editor-v2-edit-card v2-glass-surface" : "mb-3"}>
                    <Card.Body>
                        <div className={"text-right"}>
                            <button className={"btn btn-danger"} onClick={handleDeletePhoto} disabled={isDeletingPhoto}>
                                {!isDeletingPhoto && <span>Delete photo</span>}
                                {isDeletingPhoto && <span>Deleting...</span>}
                            </button>
                        </div>
                    </Card.Body>
                </Card>
            </>}
        </>
    )
}

export default PhotoEditor;
